import { useCallback, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';

import { useDepositActions } from '#/features/deposit/deposit-context';
import { logException } from '#/features/logging/logging';
import { formatUsdcBalance } from '#/features/deposit/deposit-functions';
import { useDepositModalActions } from '#/features/deposit/modal-context';
import { useDepositFormActions } from '#/features/deposit/form-context';
import useDepositToParaclear from '#/features/deposit/useDepositToParaclear';
import { publishNotification } from '#/features/notifications/topic';
import { useSystemConfig } from '#/features/system/system-config-context';

type UseDepositFunds = [
  (amount: BigNumber, asset: string) => Promise<void>,
  { readonly isBusy: boolean },
];

export function useDepositFunds(): UseDepositFunds {
  const depositToParaclear = useDepositToParaclear();
  const [isBusy, setBusy] = useState(false);

  const config = useSystemConfig();

  /**
   * Deposits the specified amout to Paraclear.
   * Toast notifications are published with status
   * updates for the deposit.
   *
   * @param amount Amount to be deposited
   */
  const depositFunds = useCallback(
    async function depositFunds(amount: BigNumber, asset: string) {
      setBusy(true);

      try {
        const token = config.getToken(asset);

        const amountFormatted = formatAmount(amount);
        publishNotification({
          kind: 'deposit-to-paraclear-in-progress',
          props: { amount: amountFormatted },
        });

        const result = await depositToParaclear(amount, token);
        if (result.ok) {
          // Keep busy until the component is taken
          // off the screen by a parent component.
        } else {
          setBusy(false);
          publishNotification({
            kind: 'deposit-failed',
            props: { amount: amountFormatted },
          });
        }
      } catch (err) {
        setBusy(false);
        const message = 'Error depositing funds to Paraclear';
        logException(new Error(message, { cause: err }));
        publishNotification({
          kind: 'deposit-unexpected-error',
          props: {},
        });
      }
    },
    [config, depositToParaclear],
  );

  return useMemo(() => [depositFunds, { isBusy }], [depositFunds, isBusy]);
}

export function useDepositToEthereumBridge() {
  const depositActions = useDepositActions();
  const formActions = useDepositFormActions();
  const modalActions = useDepositModalActions();

  const config = useSystemConfig();

  return useCallback(
    /**
     * Deposits the specified amout to Ethereum bridge.
     * Returns when the transaction is successfully sent.
     *
     * @param amount Amount to be deposited
     */
    async function depositToEthereumBridge(amount: BigNumber, asset: string) {
      try {
        const token = config.getToken(asset);

        const depositToEthereumBridgeResult =
          await depositActions.depositToEthereumBridge(token);

        if (!depositToEthereumBridgeResult.ok) return; // MetaMask will display the error

        const amountFormatted = formatAmount(amount);
        publishNotification({
          kind: 'deposit-request-sent',
          props: { amount: amountFormatted },
        });

        formActions.reset();
        modalActions.close();
      } catch (err) {
        const message = 'Error depositing funds to Ethereum Bridge';
        logException(new Error(message, { cause: err }));
      }
    },
    [config, depositActions, formActions, modalActions],
  );
}

function formatAmount(amount: BigNumber): string {
  return `${formatUsdcBalance(amount)} USDC`;
}
